import { useMemo, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, Users, AlertCircle, AlertTriangle } from 'lucide-react';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { EmployeeFilters } from './EmployeeFilters';
import { useToast } from '@/hooks/use-toast';
import type { EmployeePayrollData, EmployeeSelectionFilters, PayrollEligibilityReason } from '@/services/payroll/types';

const MAX_EMPLOYEES_PER_RUN = 50;

const REASON_LABELS: Record<PayrollEligibilityReason, string> = {
  time: 'Approved time',
  salary: 'Pay rule',
  leave: 'Paid leave',
};

interface EmployeeSelectorProps {
  employees: EmployeePayrollData[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export const EmployeeSelector = ({
  employees,
  selectedIds,
  onSelectionChange,
}: EmployeeSelectorProps) => {
  const { currencyCode } = useWorkplaceCurrency();
  const { toast } = useToast();
  const [filters, setFilters] = useState<EmployeeSelectionFilters>({
    searchQuery: '',
    roles: [],
    hasApprovedEntries: true,
  });

  const availableRoles = useMemo(() => {
    const roles = new Set(employees.map(emp => emp.role));
    return Array.from(roles).sort();
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const searchLower = filters.searchQuery.toLowerCase();
      const matchesSearch = !searchLower || 
        employee.first_name.toLowerCase().includes(searchLower) ||
        employee.last_name.toLowerCase().includes(searchLower) ||
        employee.email.toLowerCase().includes(searchLower);
      const matchesRole = filters.roles.length === 0 || filters.roles.includes(employee.role);
      const matchesApproved = !filters.hasApprovedEntries || employee.has_approved_entries;
      return matchesSearch && matchesRole && matchesApproved;
    });
  }, [employees, filters]);

  const remainingCapacity = MAX_EMPLOYEES_PER_RUN - selectedIds.length;
  const isAtCap = selectedIds.length >= MAX_EMPLOYEES_PER_RUN;

  const handleSelectAll = () => {
    // Account for already-selected employees outside the current filtered set
    const alreadySelectedOutsideFilter = selectedIds.filter(
      id => !filteredEmployees.some(emp => emp.profile_id === id)
    );
    const slotsAvailable = MAX_EMPLOYEES_PER_RUN - alreadySelectedOutsideFilter.length;
    const toSelect = filteredEmployees.slice(0, slotsAvailable).map(emp => emp.profile_id);
    const newSelection = [...alreadySelectedOutsideFilter, ...toSelect];
    onSelectionChange(newSelection);

    if (filteredEmployees.length > slotsAvailable) {
      toast({
        title: `Selection capped at ${MAX_EMPLOYEES_PER_RUN}`,
        description: `Selected ${toSelect.length} of ${filteredEmployees.length} filtered employees. Run additional batches for the rest.`,
        variant: 'destructive',
      });
    }
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const handleToggleEmployee = (profileId: string) => {
    if (selectedIds.includes(profileId)) {
      onSelectionChange(selectedIds.filter(id => id !== profileId));
    } else {
      if (isAtCap) {
        toast({
          title: `Limit reached (${MAX_EMPLOYEES_PER_RUN})`,
          description: `You can include up to ${MAX_EMPLOYEES_PER_RUN} employees per payroll run. Deselect someone or run an additional batch.`,
          variant: 'destructive',
        });
        return;
      }
      onSelectionChange([...selectedIds, profileId]);
    }
  };

  const isAllSelected = filteredEmployees.length > 0 && 
    filteredEmployees.every(emp => selectedIds.includes(emp.profile_id));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const selectedCount = selectedIds.length;
  const filteredCount = filteredEmployees.length;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Select Employees
              </CardTitle>
              <CardDescription className="mt-1.5">
                Choose up to {MAX_EMPLOYEES_PER_RUN} employees per payroll run. Run additional batches for more.
                {filteredCount !== employees.length && (
                  <span className="text-primary font-medium"> • {filteredCount} filtered from {employees.length} total</span>
                )}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={isAllSelected || filteredEmployees.length === 0 || isAtCap}
                className="whitespace-nowrap"
              >
                Select All {filteredCount > 0 && `(${Math.min(filteredCount, remainingCapacity)})`}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeselectAll}
                disabled={selectedIds.length === 0}
              >
                Deselect All
              </Button>
            </div>
          </div>

          {/* Capacity indicator */}
          <div className={`flex items-center gap-2 p-3 rounded-lg border ${
            isAtCap
              ? 'bg-destructive/10 border-destructive/20'
              : selectedCount > 0
                ? 'bg-primary/10 border-primary/20'
                : 'bg-muted/50 border-border'
          }`}>
            {isAtCap ? (
              <>
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-sm font-medium text-destructive">
                  {selectedCount}/{MAX_EMPLOYEES_PER_RUN} selected — limit reached. Run additional batches for remaining employees.
                </span>
              </>
            ) : selectedCount > 0 ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">
                  {selectedCount}/{MAX_EMPLOYEES_PER_RUN} {selectedCount === 1 ? 'employee' : 'employees'} selected
                </span>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">
                0/{MAX_EMPLOYEES_PER_RUN} selected
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-4">
              <EmployeeFilters
                filters={filters}
                onFiltersChange={setFilters}
                availableRoles={availableRoles}
              />
            </div>
          </div>

          {/* Employee List */}
          <div className="lg:col-span-3">
            {filteredEmployees.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No employees found</h3>
                <p className="text-muted-foreground max-w-md">
                  {filters.searchQuery || filters.roles.length > 0
                    ? 'Try adjusting your filters to see more results.'
                    : 'No employees with approved time entries for this period.'}
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {filteredCount} {filteredCount === 1 ? 'employee' : 'employees'}
                  </p>
                </div>
                <Separator className="mb-4" />
                <ScrollArea className="h-[550px] pr-4">
                  <div className="space-y-2">
                    {filteredEmployees.map((employee) => {
                      const isSelected = selectedIds.includes(employee.profile_id);
                      const isDisabled = !isSelected && isAtCap;
                      return (
                        <div
                          key={employee.profile_id}
                          onClick={() => !isDisabled && handleToggleEmployee(employee.profile_id)}
                          className={`group flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                            isDisabled
                              ? 'opacity-50 cursor-not-allowed bg-muted/30 border-border'
                              : isSelected
                                ? 'bg-primary/5 border-primary shadow-sm cursor-pointer'
                                : 'bg-card border-border hover:border-primary/50 hover:shadow-sm cursor-pointer'
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={() => !isDisabled && handleToggleEmployee(employee.profile_id)}
                            className="pointer-events-none"
                          />
                          <Avatar className="h-12 w-12 border-2 border-border group-hover:border-primary/50 transition-colors">
                            <AvatarImage src={employee.avatar_url} />
                            <AvatarFallback className="font-semibold">
                              {employee.first_name[0]}
                              {employee.last_name[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-base truncate">
                                {employee.first_name} {employee.last_name}
                              </p>
                              <Badge variant="secondary" className="text-xs uppercase font-medium">
                                {employee.role}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {employee.email}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {(employee.eligibility_reasons || []).map((reason) => (
                                <Badge key={reason} variant="outline" className="text-[10px] font-medium">
                                  {REASON_LABELS[reason]}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-baseline gap-1 justify-end">
                              <p className="text-lg font-bold tabular-nums">
                                {employee.total_hours.toFixed(2)}
                              </p>
                              <span className="text-xs text-muted-foreground">hrs</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground">estimate</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
